import {
  ApiBlueprint,
  analyticsApiRef,
  createFrontendModule,
  discoveryApiRef,
  fetchApiRef,
  type AnalyticsApi,
  type AnalyticsEvent,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';

/**
 * Sends a page-view beacon on every navigation.
 *
 * Backstage already fires a `navigate` analytics event on each route change, so
 * implementing AnalyticsApi is the supported way in — no router wrapper, no
 * component mounted at the root, and it keeps working if the app's routing
 * changes underneath us.
 *
 * Only `navigate` is forwarded. Backstage also emits click/search/filter events
 * and those are genuinely useful, but they are a different question with a much
 * higher volume, and shipping them into the same table would make "page views"
 * mean something other than page views on every board that reads it.
 *
 * Identity is deliberately NOT sent: the backend resolves it from the request
 * credentials. A client-supplied email would let anyone attribute traffic to
 * anyone.
 *
 * Every failure is swallowed. Analytics must never break navigation.
 */
class PageviewAnalyticsApi implements AnalyticsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  captureEvent(event: AnalyticsEvent): void {
    if (event.action !== 'navigate') return;

    const path = typeof event.subject === 'string' ? event.subject : '';
    if (!path.startsWith('/') || path.startsWith('/api')) return;

    void this.send(path);
  }

  private async send(path: string): Promise<void> {
    try {
      const base = await this.discoveryApi.getBaseUrl('analytics');
      // fetchApi, not window.fetch: it attaches the Backstage identity token,
      // which is what lets the backend attribute the view server-side.
      await this.fetchApi.fetch(`${base}/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path }),
        keepalive: true,
      });
    } catch {
      /* analytics must never break navigation */
    }
  }
}

const pageviewAnalyticsApi = ApiBlueprint.make({
  name: 'pageview-analytics',
  params: defineParams =>
    defineParams({
      api: analyticsApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) =>
        new PageviewAnalyticsApi(discoveryApi, fetchApi),
    }),
});

export const analyticsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [pageviewAnalyticsApi],
});
